/**
 * Generate a unique 6-digit numeric SKU for an item
 * Format: NNNNNN (6 digits)
 * Example: 123456
 */
export const generateSKU = () => {
  // Generate random 6-digit number (100000 to 999999)
  const min = 100000;
  const max = 999999;
  const sku = Math.floor(Math.random() * (max - min + 1)) + min;
  return String(sku);
};

/**
 * Generate a variation SKU based on parent SKU
 * Format: PARENT_SKU-COLOR_CODE-SIZE_CODE
 * Example: 123456-BLU-XL
 */
export const generateVariationSKU = (parentSKU, color, size) => {
  const colorCode = color
    .replace(/[^a-zA-Z]/g, '')
    .substring(0, 3)
    .toUpperCase()
    .padEnd(3, 'X');

  const sizeCode = size
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();

  return `${parentSKU}-${colorCode}-${sizeCode}`;
};

/**
 * Validate SKU format
 * Item SKU: 6 digits (e.g., 123456)
 * Variation SKU: 6 digits-COLOR-SIZE (e.g., 123456-BLU-XL)
 */
export const validateSKU = (sku) => {
  // Item SKU: exactly 6 digits
  const itemPattern = /^\d{6}$/;
  // Variation SKU: 6 digits followed by -COLOR-SIZE
  const variationPattern = /^\d{6}-[A-Z]{3}-[A-Z0-9]+$/;

  return itemPattern.test(sku) || variationPattern.test(sku);
};

/**
 * Generate a unique SKU with verification
 * Checks against existing items to ensure uniqueness
 * @param {Function} checkSKUExists - Function that returns true if SKU exists
 * @param {number} maxRetries - Maximum number of generation attempts (default: 10)
 * @returns {Promise<string>} A unique 6-digit SKU
 */
export const generateUniqueSKU = async (checkSKUExists, maxRetries = 10) => {
  for (let i = 0; i < maxRetries; i++) {
    const sku = generateSKU();
    const exists = await checkSKUExists(sku);

    if (!exists) {
      return sku;
    }

    console.log(`SKU ${sku} already exists, generating new one (attempt ${i + 1}/${maxRetries})`);
  }

  throw new Error(`Failed to generate unique SKU after ${maxRetries} attempts`);
};
